"use client";
import { useEffect, useState } from "react";
import { getBranchTime } from "@/lib/branch-time";
import { useBranch } from "@/store/branch";
import type { HoursSet } from "@/api/online-ordering-service/model/hoursSet";
import type { OperatingHours } from "@/api/online-ordering-service/model/operatingHours";

const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const satisfies readonly (keyof OperatingHours)[];

type Parsed = { open: number; close: number; openStr: string };

function parseRanges(set: HoursSet | null | undefined): Parsed[] {
  if (!set?.ranges?.length) return [];
  const out: Parsed[] = [];
  for (const r of set.ranges) {
    const [oh, om] = r.open.split(":").map(Number);
    const [ch, cm] = r.close.split(":").map(Number);
    if (Number.isNaN(oh) || Number.isNaN(ch)) continue;
    const open = oh * 60 + (om || 0);
    let close = ch * 60 + (cm || 0);
    // Close at or before open means the range runs past midnight. The backend
    // allows close "00:00" (= midnight) and overnight ranges like 18:00-02:00;
    // without this a branch open 08:00-00:00 showed closed all day.
    if (close <= open) close += 24 * 60;
    out.push({
      open,
      close,
      openStr: `${String(oh).padStart(2, "0")}:${String(om || 0).padStart(2, "0")}`,
    });
  }
  return out.sort((a, b) => a.open - b.open);
}

/** Open at minute `min` of day `dayIdx`, counting yesterday's overnight ranges. */
function isOpenAtMinute(
  operatingHours: OperatingHours,
  dayIdx: number,
  min: number,
): boolean {
  for (const r of parseRanges(operatingHours[DAY_KEYS[dayIdx]])) {
    if (min >= r.open && min < r.close) return true;
  }
  const prev = parseRanges(operatingHours[DAY_KEYS[(dayIdx + 6) % 7]]);
  for (const r of prev) {
    if (r.close > 24 * 60 && min + 24 * 60 < r.close) return true;
  }
  return false;
}


function formatHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export type BranchOpenState = {
  isClosed: boolean;
  /** Pretty time the branch will next open (e.g. "09:00 AM"), or null. */
  opensAt: string | null;
  /** Branch has operating hours configured at all. */
  hasHours: boolean;
};

/**
 * Computes branch open/closed state from the active branch's operating hours.
 * Re-evaluates every minute.
 */
export function useBranchOpen(): BranchOpenState {
  const { branch } = useBranch();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  // tick is intentionally referenced to drive recompute on schedule.
  void tick;

  const branchRecord = branch as
    | { isOpenNow?: boolean | null; status?: string | null }
    | null;
  if (branchRecord?.isOpenNow === false) {
    return { isClosed: true, opensAt: null, hasHours: false };
  }
  const status = branchRecord?.status?.toLowerCase?.() ?? "";
  if (
    status === "closed" ||
    status === "offline" ||
    status === "unavailable" ||
    status === "inactive"
  ) {
    return { isClosed: true, opensAt: null, hasHours: false };
  }
  const branchHours = branch as
    | { operatingHours?: OperatingHours | null; timezone?: string | null }
    | null;
  const operatingHours = branchHours?.operatingHours ?? null;
  const timezone = branchHours?.timezone ?? null;

  if (!operatingHours) {
    return { isClosed: false, opensAt: null, hasHours: false };
  }

  const branchTime = getBranchTime(timezone);
  const todayIdx = branchTime.getDay();
  const nowMin = branchTime.getHours() * 60 + branchTime.getMinutes();

  const todayRanges = parseRanges(operatingHours[DAY_KEYS[todayIdx]]);

  // Currently open?
  if (isOpenAtMinute(operatingHours, todayIdx, nowMin)) {
    return { isClosed: false, opensAt: null, hasHours: true };
  }

  // Find next opening today
  const nextToday = todayRanges.find((r) => r.open > nowMin);
  if (nextToday) {
    return {
      isClosed: true,
      opensAt: formatHHMM(nextToday.open),
      hasHours: true,
    };
  }

  // Otherwise scan upcoming days (up to 7) for the next opening
  for (let i = 1; i <= 7; i += 1) {
    const dayIdx = (todayIdx + i) % 7;
    const ranges = parseRanges(operatingHours[DAY_KEYS[dayIdx]]);
    if (ranges.length > 0) {
      return {
        isClosed: true,
        opensAt: formatHHMM(ranges[0].open),
        hasHours: true,
      };
    }
  }

  // Hours object exists but every day is empty — treat as closed indefinitely.
  return { isClosed: true, opensAt: null, hasHours: true };
}

/**
 * Checks whether the branch will be open at a given specific target Date.
 */
export function isBranchOpenAt(
  branch: unknown,
  targetDate: Date,
): boolean {
  if (!branch) return true;
  const branchRecord = branch as {
    status?: string | null;
    operatingHours?: OperatingHours | null;
    timezone?: string | null;
  } | null;

  const status = branchRecord?.status?.toLowerCase?.() ?? "";
  if (
    status === "closed" ||
    status === "offline" ||
    status === "unavailable" ||
    status === "inactive"
  ) {
    return false;
  }

  const operatingHours = branchRecord?.operatingHours ?? null;
  const timezone = branchRecord?.timezone ?? null;
  if (!operatingHours) return true;

  let branchTime: Date;
  if (!timezone) {
    branchTime = targetDate;
  } else {
    try {
      branchTime = new Date(
        targetDate.toLocaleString("en-US", { timeZone: timezone }),
      );
    } catch {
      branchTime = targetDate;
    }
  }

  const dayIdx = branchTime.getDay();
  const targetMin = branchTime.getHours() * 60 + branchTime.getMinutes();
  return isOpenAtMinute(operatingHours, dayIdx, targetMin);
}
